import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { ArrowsLeftRight, Coins, ChartLineUp, Trophy, Wallet } from "@phosphor-icons/react";
import { card, btnPrimary, usd, errMsg, TradeBackdrop } from "./shared";

function StatTile({ icon: Icon, label, value, testid, sub }) {
  return (
    <div className={`${card} p-5`} data-testid={testid}>
      <div className="flex items-center gap-2 text-sky-600 text-xs font-bold uppercase tracking-widest">
        <Icon size={16} weight="bold" /> {label}
      </div>
      <p className="font-heading text-3xl font-black text-sky-950 mt-2">{value}</p>
      {sub && <p className="text-xs text-sky-700/70 mt-1">{sub}</p>}
    </div>
  );
}

export function RewardDashboard({ data, reload }) {
  const [busy, setBusy] = useState(false);
  const pct = Math.round((data.points_in_current_share / data.points_per_share) * 100);

  const transfer = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/market/transfer");
      toast.success(`${usd(r.amount)} moved to your wallet`);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-sky-500 via-sky-400 to-cyan-300 p-6 md:p-8 text-white shadow-xl" data-testid="market-hero">
        <TradeBackdrop />
        <div className="relative">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-white/80">All-time spending</p>
          <p className="font-heading text-5xl md:text-6xl font-black mt-2" data-testid="market-total-spend">{usd(data.total_spend_usd)}</p>
          <p className="text-sm text-white/85 mt-2">
            Credits {usd(data.credit_spend_usd)} · Businesses {usd(data.business_spend_usd)}
          </p>
          <div className="mt-6 grid grid-cols-2 gap-4 max-w-md">
            <div className="rounded-2xl bg-white/20 backdrop-blur-md p-4">
              <p className="text-[11px] uppercase tracking-widest font-bold text-white/80">Reward shares</p>
              <p className="font-heading text-5xl font-black leading-none mt-1" data-testid="market-shares">{data.shares}</p>
            </div>
            <div className="rounded-2xl bg-white/20 backdrop-blur-md p-4">
              <p className="text-[11px] uppercase tracking-widest font-bold text-white/80">Points</p>
              <p className="font-heading text-5xl font-black leading-none mt-1" data-testid="market-points">{data.points}</p>
            </div>
          </div>
        </div>
      </div>

      <div className={`${card} p-5`} data-testid="market-share-progress">
        <div className="flex justify-between text-sm font-semibold text-sky-900">
          <span>{data.points_in_current_share} / {data.points_per_share} points to next share</span>
          <span>{pct}%</span>
        </div>
        <div className="h-3 rounded-full bg-sky-100 mt-3 overflow-hidden">
          <div className="h-full rounded-full bg-gradient-to-r from-sky-400 to-cyan-400 transition-[width] duration-700" style={{ width: `${pct}%` }} />
        </div>
        <p className="text-xs text-sky-700/70 mt-2">
          Every {usd(data.usd_per_point)} spent = 1 point · {data.points_per_share} points = 1 reward share. Shares never reset.
        </p>
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <StatTile icon={Trophy} label="Value per share" testid="market-per-share"
          value={data.last_reward ? usd(data.last_reward.per_share_usd) : "—"}
          sub={data.last_reward ? `Last payout ${usd(data.last_reward.amount_usd)} ÷ ${data.last_reward.total_shares} shares` : "No payout yet"} />
        <StatTile icon={ChartLineUp} label="Earned all-time" value={usd(data.reward_earned_total_usd)} testid="market-earned-total" />
        <StatTile icon={Coins} label="Your credits" value={data.credits.toLocaleString()} testid="market-credits" />
      </div>

      <div className={`${card} p-6 flex flex-col md:flex-row md:items-center justify-between gap-4`}>
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-sky-600 flex items-center gap-2"><Wallet size={16} weight="bold" /> Reward share earnings</p>
          <p className="font-heading text-5xl font-black text-sky-950 mt-1" data-testid="market-unclaimed">{usd(data.reward_unclaimed_usd)}</p>
          <p className="text-xs text-sky-700/70 mt-1">Only the amount moves — your shares stay and keep growing.</p>
        </div>
        <button data-testid="market-transfer-btn" className={btnPrimary} onClick={transfer}
          disabled={busy || data.reward_unclaimed_usd <= 0}>
          <ArrowsLeftRight size={18} weight="bold" /> {busy ? "Transferring…" : "Transfer"}
        </button>
      </div>

      <div className={`${card} p-5 text-sm text-sky-900`} data-testid="market-my-ids">
        Businesses can add your purchases using your Account ID <b className="font-mono">{data.user_id}</b>
        {data.referral_code && <> or Referral ID <b className="font-mono">{data.referral_code}</b></>}.
      </div>
    </div>
  );
}
